import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { useOnError } from 'src/utils/errors';
import { ICON_MAP } from 'src/utils/constants';
import { GeneralASError } from 'src/registry/types/axios';
import { cn } from 'src/utils';
import FinancialDocumentRowForm from 'src/features/financialDocumentRows/components/forms/FinancialDocumentRowForm';
import { FinancialDocumentRow } from 'src/features/financialDocumentRows/utils/types';
import { useGetFinancialDocumentRow } from 'src/features/financialDocumentRows/api/useGetFinancialDocumentRow';
import {
    financialDocumentRowFormSchema,
    FinancialDocumentRowFormSchemaValues,
} from 'src/features/financialDocumentRows/utils/formSchema';
import { useListFinancialDocumentRows } from 'src/features/financialDocumentRows/api/useListFinancialDocumentRows';
import { useParams } from 'react-router-dom';

interface FinancialDocumentRowFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedRow?: FinancialDocumentRow;
    children?: React.ReactNode;
    isOpen?: boolean;
    isPending: boolean;
    icon: IconProp;
    title: string;
    subtitle: string;
    mutate: (
        data: FinancialDocumentRowFormSchemaValues,
        onSuccess: () => void,
        onError: (error: GeneralASError) => void,
    ) => void;
}

function FinancialDocumentRowFormModal<TData>({
    selectedRow,
    isPending,
    children,
    className,
    icon,
    title,
    subtitle,
    mutate,
}: FinancialDocumentRowFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { financialDocumentId } = useParams();

    const { data: financialDocumentRow } = useGetFinancialDocumentRow({
        rowId: selectedRow?.id ?? '',
        config: { enabled: !!selectedRow?.id && isModalOpen },
    });

    const { data: listFinancialDocumentRows } = useListFinancialDocumentRows({
        financial_document: financialDocumentId,
    });

    const rowCount = React.useMemo(() => {
        const countOfRows = listFinancialDocumentRows?.count ? listFinancialDocumentRows?.count : 0;
        return countOfRows + 1;
    }, [listFinancialDocumentRows?.count]);

    const onError = useOnError();

    const defaultValues = React.useMemo(() => {
        const percent = Number(financialDocumentRow?.tax_percent);

        const price = financialDocumentRow?.price ? financialDocumentRow?.price : undefined;
        const priceWithTax = price ? price * (1 + percent) : undefined;

        return {
            number: financialDocumentRow?.number ?? rowCount,
            name: financialDocumentRow?.name ?? undefined,
            quantity: financialDocumentRow?.quantity ?? undefined,
            tax_reason: financialDocumentRow?.tax_reason.id ?? undefined,
            tax_percent: financialDocumentRow?.tax_percent
                ? (Number(financialDocumentRow.tax_percent) * 100).toFixed(2)
                : undefined,
            price: financialDocumentRow?.price ?? undefined,
            price_with_tax: priceWithTax ?? undefined,
            financial_document: financialDocumentRow?.financial_document?.id ?? undefined,
            unit: financialDocumentRow?.unit?.id ?? undefined,
            accounting_heading: financialDocumentRow?.accounting_heading?.id ?? undefined,
        };
    }, [financialDocumentRow, rowCount]);

    const form = useForm<FinancialDocumentRowFormSchemaValues>({
        resolver: zodResolver(financialDocumentRowFormSchema),
        defaultValues,
    });

    React.useEffect(() => {
        form.reset(defaultValues);
    }, [form, isModalOpen, defaultValues]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();
        setIsModalOpen(isOpen);
    };

    const onSubmit = React.useCallback(
        (values: FinancialDocumentRowFormSchemaValues) => {
            const { tax_percent, price_with_tax, ...rest } = values;

            mutate(
                { ...rest },
                () => setIsModalOpen(false),
                (error) => onError(error, form.setError),
            );
        },
        [mutate, onError, form.setError],
    );

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <DialogTrigger asChild>
                    <Button className={cn('ml-auto', className)}>
                        <FontAwesomeIcon icon={ICON_MAP.ADD} />
                        {title}
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={icon} />
                        <div>
                            <DialogTitle>{title}</DialogTitle>
                            <DialogDescription>{subtitle}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <FinancialDocumentRowForm form={form} onSubmit={onSubmit} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending || (selectedRow && !form.formState.isDirty)}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default FinancialDocumentRowFormModal;
